// ============================================================================
// Feuille du noyau : SOMMES DE RIEMANN (refonte, lot 6.64, moteur v59 —
// docs/moteur/REFONTE_MOTEUR.md §5.84).
// ============================================================================
// L'énoncé : une suite définie par une somme (ou un produit), « Sₙ = Σ(k = 1 ; n) n ÷ (n² + k²) »,
// dont on cherche la limite. L'élève écrit, une étape par ligne :
//
//   « Sₙ = (1 ÷ n) × Σ(k = 1 ; n) 1 ÷ (1 + (k ÷ n)²) »   faire apparaître f(k ÷ n)          1
//   « Sₙ = Σ(j = 1 ; n) 1 ÷ (n + j) »                    changer d'indice                    1
//   « ln(Pₙ) = (1 ÷ n) × Σ(k = 1 ; n) ln(1 + k ÷ n) »    passer au logarithme (Π → Σ)        2
//   « Sₙ → ∫(0 ; 1) 1 ÷ (1 + x²) dx »                    somme de Riemann                    2
//   « ∫(0 ; 1) … dx = [ … ](0 ; 1) », « u(x) = … »…      l'intégrale : feuille d'intégrales  (lot 6.60)
//   « Sₙ → π ÷ 4 »                                       conclure                            1
//   « Pₙ → e^(2ln(2) − 1) »                              composer par exp                    1
//   « Pₙ → 4 ÷ e »                                       calculer                            1
//
// Une somme s'écrit avec un bout « Σ(k = 1 ; n) » (l'indice, ses bornes) suivi de ce qu'on
// additionne : tout ce qui suit jusqu'au prochain « + » ou « − » (une somme à additionner se
// met entre parenthèses). « Π(k = 1 ; n) » de même pour un produit.
//
// Vérité : deux écritures d'une suite sont égales quand elles ont la même valeur pour
// n = 1, 2, …, 24 (preuve ÉCHANTILLONNÉE, calculs en flottants) ; une limite est vraie quand
// c'est la limite de la question (calculée par la somme de Riemann, contrôlée par la porte).
// Une ligne juste qui saute une étape reste sur la feuille sans HORA (D7). Une ligne fausse
// ne dit jamais la limite.
//
// Pur : aucune dépendance à l'app.
// ============================================================================

import type { Chip } from './lexer';
import {
  checkSyntax,
  isConst,
  isFunc,
  isNum,
  isOperand,
  isPow,
  isSupPow,
  isVar,
  lex,
  normalize,
} from './lexer';
import type { ScratchModel } from './model';
import { atomsText, type Line } from './tree';
import {
  canon,
  num,
  parseStatement,
  substitute,
  symbolsOf,
  termKey,
  type Term,
} from './kernel/term';
import { evalFloat, withRadianArcs } from './kernel/normal';
import { constOps, constShapeKey } from './kernel-formula';
import {
  createKIntState,
  integrateNum,
  isBracketClose,
  isDiffAtom,
  isIntegralAtom,
  kintCommit,
  kintEstablished,
  kintHolds,
  kintJudge,
  kintLineAtoms,
  kintStartComputed,
  kintText,
  type KIntState,
  type KIntVerdict,
} from './kernel-integral';

/* ------------------------------ les pas ------------------------------ */

export const RIEMANN_STEP_IDS = {
  rewrite: 'riemann.rewrite@1',
  reindex: 'riemann.reindex@1',
  logPower: 'riemann.log_power@1',
  logProduct: 'riemann.log_product@1',
  riemann: 'riemann.riemann@1',
  conclude: 'riemann.conclude@1',
  compose: 'riemann.compose@1',
  compute: 'kernel.compute@1',
} as const;
type StepKind = keyof typeof RIEMANN_STEP_IDS;

const STEP: Record<StepKind, { label: string; hora: number; why: string }> = {
  rewrite: {
    label: 'Faire apparaître f(k ÷ n)',
    hora: 1,
    why: 'On met 1 ÷ n en facteur et on écrit ce qu’on additionne comme une fonction de k ÷ n : (1 ÷ n) × Σ f(k ÷ n).',
  },
  reindex: {
    label: 'Changer d’indice',
    hora: 1,
    why: 'On renomme l’indice (j = k − n…) : les mêmes termes, additionnés autrement.',
  },
  logPower: {
    label: 'Logarithme d’une puissance',
    hora: 1,
    why: 'ln(aᵇ) = b × ln(a).',
  },
  logProduct: {
    label: 'Logarithme d’un produit',
    hora: 1,
    why: 'Le logarithme d’un produit est la somme des logarithmes : le produit devient une somme.',
  },
  riemann: {
    label: 'Somme de Riemann',
    hora: 2,
    why: 'Si f est continue sur [a ; b], (1 ÷ n) × Σ f(k ÷ n), pour k ÷ n allant de a à b, tend vers ∫(a ; b) f(x) dx.',
  },
  conclude: {
    label: 'Conclure',
    hora: 1,
    why: 'La suite tend vers l’intégrale, et l’intégrale est calculée.',
  },
  compose: {
    label: 'Composer par exp',
    hora: 1,
    why: 'exp est continue : si ln(uₙ) tend vers ℓ, uₙ = exp(ln(uₙ)) tend vers exp(ℓ).',
  },
  compute: {
    label: 'Calculer',
    hora: 1,
    why: 'On calcule la valeur de la limite.',
  },
};

const CARD = 'Sommes de Riemann';
export const ARROW = '→';

/* ------------------------------ types ------------------------------ */

export class RiemannModelError extends Error {}

interface SumAtom {
  sign: 'Σ' | 'Π';
  /** l'indice (« k ») */
  k: string;
  lo: Term;
  hi: Term;
  body: Term;
  /** clé de l'écriture (bornes et terme) */
  key: string;
}

interface IntAtom {
  lo: Term;
  hi: Term;
  a: number;
  b: number;
  x: string;
  body: Term;
  /** « ∫(0 ; 1) 1 ÷ (1 + x²) dx » */
  text: string;
}

/** une écriture d'une suite : l'expression, ses sommes et ses intégrales remplacées par des noms */
interface RForm {
  outer: Term;
  sums: Map<string, SumAtom>;
  ints: Map<string, IntAtom>;
  /** écriture (comparaison « déjà écrit ») */
  wkey: string;
}

interface Riem {
  lambda: number;
  a: number;
  b: number;
  g: (x: number) => number;
}

interface Sub {
  ks: KIntState;
  /** l'intégrale (son écriture) */
  text: string;
  value: number;
}

interface Lim {
  subject: Term;
  value: number;
  /** la suite tend vers coef × une intégrale (sous-feuille) */
  ints: { sub: number; coef: number }[];
  /** les écritures de la limite (valeurs) */
  writings: { t: Term; wkey: string; ops: number }[];
}

type RMove =
  | { k: 'eq'; skey: string; subject: Term; forms: RForm[]; text: string }
  | {
      k: 'riem';
      skey: string;
      subject: Term;
      value: number;
      int: string;
      coef: number;
      text: string;
    }
  | { k: 'val'; skey: string; subject: Term; value: number; t: Term; text: string; final: boolean }
  | { k: 'int'; sub: number; fresh?: string; v: KIntVerdict; text: string };

export interface KRiemVerdict {
  kind:
    | 'empty'
    | 'open'
    | 'building'
    | 'ready'
    | 'pending'
    | 'syntax'
    | 'trap'
    | 'false'
    | 'unjustified'
    | 'useless'
    | 'step';
  atoms: string[];
  title?: string;
  message?: string;
  badChip?: number;
  complete?: boolean;
  line?: Line;
  final?: boolean;
  hora?: number;
  jump?: boolean;
  trap?: { line: Line; title: string; why: string };
  rule?: { id: string; card: string; label: string; hora: number; why: string };
  rmove?: RMove;
}

export interface KRiemState {
  model: ScratchModel;
  /** le nom de la suite (« Sₙ ») */
  X: string;
  start: RForm;
  startText: string;
  answer: Term;
  answerValue: number;
  answerOps: number;
  /** écritures établies de chaque expression de la suite (clé de l'expression) */
  forms: Map<string, { subject: Term; forms: RForm[] }>;
  lims: Map<string, Lim>;
  subs: Sub[];
  written: Set<string>;
  givenTexts: Set<string>;
  path: string[];
  /** les lignes du corrigé qui calculent l'intégrale */
  intPath: string[];
  traps: { text: string; line: Line; title: string; why: string }[];
  lines: number;
  concluded: boolean;
}

/* ------------------------------ les bouts ------------------------------ */

const SUM_RE = /^([ΣΠ])\(([a-z]) = (.+) ; (.+)\)$/;
const NAME_RE = /^([uv])(′?)\(([a-zθ])\)$/;

export const isSumAtom = (a: string | undefined): boolean => !!a && SUM_RE.test(a);

/** Le bout « Σ(k = 1 ; n) » : son signe, son indice, ses bornes (affichage) ; null sinon. */
export function sumBoundsOf(
  a: string | undefined,
): { kind: 'sum' | 'prod'; index: string; lo: string; hi: string } | null {
  if (!a) return null;
  const m = SUM_RE.exec(a);
  if (!m) return null;
  return { kind: m[1] === 'Σ' ? 'sum' : 'prod', index: m[2]!, lo: m[3]!, hi: m[4]! };
}

/** une borne écrite comme le moteur la relit, « 2n » plutôt que « 2 × n » */
function boundText(s: string): string {
  return atomsText(normalize(lex(s.trim())))
    .replace(/\//g, ' ÷ ')
    .replace(/\s*÷\s*/g, ' ÷ ')
    .replace(/(\d) × (?=[a-zα-ω])/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
}
/** « Σ(k = 1 ; n) » */
export function sumAtom(sign: 'Σ' | 'Π', k: string, lo: string, hi: string): string {
  return `${sign}(${k} = ${boundText(lo)} ; ${boundText(hi)})`;
}

function group(text: string, open: number): { inner: string; end: number } | null {
  let depth = 0;
  for (let i = open; i < text.length; i++) {
    const c = text[i]!;
    if (c === '(') depth++;
    else if (c === ')') {
      depth--;
      if (depth === 0) return { inner: text.slice(open + 1, i), end: i + 1 };
    }
  }
  return null;
}
function splitBounds(inner: string): [string, string] | null {
  let depth = 0;
  for (let i = 0; i < inner.length; i++) {
    const c = inner[i]!;
    if (c === '(') depth++;
    else if (c === ')') depth--;
    else if (c === ';' && depth === 0) return [inner.slice(0, i), inner.slice(i + 1)];
  }
  return null;
}

const endsOp = (a: string): boolean =>
  isOperand(a) || a === ')' || a === '²' || isSupPow(a) || a === '!' || isPow(a);
const startsOp = (a: string): boolean =>
  isVar(a) || isConst(a) || isFunc(a) || a === '(' || a === '√' || isNum(a);

/**
 * Multiplication sous-entendue : comme les intégrales (jamais contre « dx », ni après un
 * « ∫( »), et jamais après un « Σ(…) » (ce qu'on additionne le suit).
 */
export function kriemNormalize(atoms: string[]): { atoms: string[]; src: number[] } {
  const out: string[] = [];
  const src: number[] = [];
  atoms.forEach((a, i) => {
    const p = out[out.length - 1];
    if (p !== undefined && p !== '×') {
      const pOpen = isIntegralAtom(p) || p === '[' || isSumAtom(p);
      const pCloses = isBracketClose(p) || isDiffAtom(p);
      let times = false;
      if (isIntegralAtom(a) || a === '[' || isSumAtom(a)) times = !pOpen && (pCloses || endsOp(p));
      else if (isBracketClose(a) || isDiffAtom(a)) times = false;
      else if (pCloses) times = startsOp(a) || isNum(a);
      else if (!pOpen) times = endsOp(p) && startsOp(a) && !isNum(a);
      if (times) {
        out.push('×');
        src.push(i);
      }
    }
    out.push(a);
    src.push(i);
  });
  return { atoms: out, src };
}

/** Une ligne (texte) en bouts : « Σ(k = 1 ; n) », « → », puis comme les intégrales. */
export function kriemLineAtoms(text: string): string[] {
  const t = String(text).replace(/->/g, ARROW);
  const out: string[] = [];
  let buf = '';
  const flush = () => {
    if (buf.trim()) out.push(...kintLineAtoms(buf));
    buf = '';
  };
  for (let i = 0; i < t.length; ) {
    const c = t[i]!;
    if ((c === 'Σ' || c === 'Π') && t[i + 1] === '(') {
      const g = group(t, i + 1);
      const b = g && splitBounds(g.inner);
      const m = b && /^\s*([a-z])\s*=\s*(.+)$/.exec(b[0]);
      if (g && b && m) {
        flush();
        out.push(sumAtom(c, m[1]!, m[2]!, b[1]));
        i = g.end;
        continue;
      }
    }
    if (c === ARROW) {
      flush();
      out.push(ARROW);
      i++;
      continue;
    }
    buf += c;
    i++;
  }
  flush();
  return kriemNormalize(out).atoms;
}

/** L'écriture d'une ligne (enregistrement, comparaison). */
export function kriemText(atoms: string[]): string {
  const side = (A: string[]): string => {
    const parts: string[] = [];
    let run: string[] = [];
    const flush = () => {
      if (run.length) parts.push(kintText(run));
      run = [];
    };
    for (const a of A) {
      if (isSumAtom(a)) {
        flush();
        parts.push(a);
      } else run.push(a);
    }
    flush();
    return parts.join(' ').replace(/\s+/g, ' ').trim();
  };
  const i = atoms.indexOf(ARROW);
  if (i < 0) return side(atoms);
  const l = side(atoms.slice(0, i));
  const r = side(atoms.slice(i + 1));
  return `${l ? l + ' ' : ''}${ARROW}${r ? ' ' + r : ''}`;
}

const lineOf = (atoms: string[]): Line => ({
  dis: [{ sides: [{ k: 's', n: kriemText(atoms) }] }],
});
const chipsOf = (atoms: string[]): Chip[] => atoms.map((a) => ({ atoms: [a], text: a }));
/** Ligne de feuille (texte) pour l'app. */
export function kriemLine(text: string): Line {
  return lineOf(kriemLineAtoms(text));
}

/* ------------------------------ termes ------------------------------ */

const E: Term = { k: 'fn', f: 'exp', a: { k: 'num', v: { n: 1n, d: 1n } } } as Term;
/** la constante e s'écrit « e » ; « e^(…) » est déjà lu exp(…) */
function withE(t: Term): Term {
  switch (t.k) {
    case 'sym':
      return t.n === 'e' ? E : t;
    case 'add':
    case 'mul':
      return { ...t, c: t.c.map(withE) } as Term;
    case 'div':
    case 'pow':
    case 'binom':
      return { ...t, a: withE(t.a), b: withE(t.b) } as Term;
    case 'fn':
      return { ...t, a: withE(t.a) } as Term;
    default:
      return t;
  }
}

function exprOf(atoms: string[]): Term | null {
  if (!atoms.length) return null;
  try {
    const p = parseStatement(atomsText(atoms));
    return p.ok && p.s.k === 'expr' ? withE(p.s.e) : null;
  } catch {
    return null;
  }
}
function exprOfText(text: string): Term | null {
  try {
    return exprOf(normalize(lex(text)));
  } catch {
    return null;
  }
}

const keyOf = (t: Term): string => {
  try {
    return termKey(canon(t));
  } catch {
    return termKey(t);
  }
};
const shapeOf = (t: Term): string => {
  try {
    return constShapeKey(t);
  } catch {
    return termKey(t);
  }
};
const opsOf = (t: Term): number => {
  try {
    return constOps(t);
  } catch {
    return 99;
  }
};

function val(t: Term, env: Record<string, number> = {}): number {
  try {
    // moteur v60 : arctan, arcsin, arccos en RADIANS (« Sₙ → arctan(1) »)
    const v = withRadianArcs(() => evalFloat(t, env));
    return Number.isFinite(v) ? v : NaN;
  } catch {
    return NaN;
  }
}
const near = (a: number, b: number, tol = 1e-9): boolean =>
  Number.isFinite(a) &&
  Number.isFinite(b) &&
  Math.abs(a - b) <= tol * Math.max(1, Math.abs(a), Math.abs(b));

/** les valeurs de n où l'on compare deux écritures */
const NS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 15, 20, 24];

function sumValue(s: SumAtom, n: number): number {
  const lo = val(s.lo, { n });
  const hi = val(s.hi, { n });
  const L = Math.round(lo);
  const H = Math.round(hi);
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return NaN;
  if (Math.abs(lo - L) > 1e-9 || Math.abs(hi - H) > 1e-9 || H - L > 20000) return NaN;
  let acc = s.sign === 'Σ' ? 0 : 1;
  for (let k = L; k <= H; k++) {
    const v = val(s.body, { [s.k]: k, n });
    if (!Number.isFinite(v)) return NaN;
    acc = s.sign === 'Σ' ? acc + v : acc * v;
  }
  return acc;
}

function formValue(f: RForm, n: number, extra: Record<string, number> = {}): number {
  const env: Record<string, number> = { n, ...extra };
  for (const [ph, s] of f.sums) {
    const v = sumValue(s, n);
    if (!Number.isFinite(v)) return NaN;
    env[ph] = v;
  }
  return val(f.outer, env);
}

/** deux suites égales (mêmes valeurs aux n d'essai) */
function sameSeq(u: (n: number) => number, v: (n: number) => number): boolean {
  let ok = 0;
  for (const n of NS) {
    const a = u(n);
    const b = v(n);
    if (Number.isNaN(a) && Number.isNaN(b)) continue;
    if (!near(a, b)) return false;
    ok++;
  }
  return ok >= 6;
}

/* ------------------------------ lecture d'une ligne ------------------------------ */

type ReadSide =
  | { k: 'incomplete' }
  | { k: 'syntax'; title: string; message: string; index: number }
  | { k: 'ok'; members: RForm[] };

const syntaxR = (title: string, message: string, index: number) =>
  ({ k: 'syntax', title, message, index }) as const;

let PH = 0;
const sub = (d: number) =>
  String(d)
    .split('')
    .map((c) => '₀₁₂₃₄₅₆₇₈₉'[Number(c)])
    .join('');

/** un côté d'une ligne : ses membres (séparés par « = »), sommes et intégrales lues à part */
function readSide(A: string[], offset: number): ReadSide {
  const sums = new Map<string, SumAtom>();
  const ints = new Map<string, IntAtom>();
  const outer: string[] = [];
  const outerSrc: number[] = [];
  for (let i = 0; i < A.length; i++) {
    const a = A[i]!;
    if (isSumAtom(a)) {
      // ce qu'on additionne : jusqu'au prochain + ou − (niveau 0), « = », ou la parenthèse
      // qui ferme un groupe ouvert avant le Σ
      let j = i + 1;
      let depth = 0;
      for (; j < A.length; j++) {
        const b = A[j]!;
        if (b === '(') depth++;
        else if (b === ')') {
          if (depth === 0) break;
          depth--;
        } else if (depth === 0 && (b === '=' || ((b === '+' || b === '−') && j > i + 1))) break;
        if (isSumAtom(b) || isIntegralAtom(b) || b === '[')
          return syntaxR(
            'Une somme à la fois',
            'Ce qu’on additionne ne contient ni autre somme, ni intégrale.',
            offset + j,
          );
      }
      const term = A.slice(i + 1, j);
      if (!term.length) {
        if (j >= A.length) return { k: 'incomplete' };
        return syntaxR(
          'Ce qu’on additionne',
          'Après « Σ(k = 1 ; n) », écris le terme de la somme.',
          offset + i,
        );
      }
      const syn = checkSyntax(normalize(term));
      if (!syn.ok)
        return syntaxR(
          'Ça commence mal',
          syn.msg,
          offset + i + 1 + Math.min(syn.index, term.length - 1),
        );
      if (!syn.complete) {
        if (j >= A.length) return { k: 'incomplete' };
        return syntaxR('Ça ne se lit pas', 'Le terme de la somme n’est pas fini.', offset + j);
      }
      const body = exprOf(normalize(term));
      const m = SUM_RE.exec(a)!;
      const lo = exprOfText(m[3]!);
      const hi = exprOfText(m[4]!);
      if (!body || !lo || !hi)
        return syntaxR('Ça ne se lit pas', 'Vérifie les parenthèses.', offset + j - 1);
      const k = m[2]!;
      if (k === 'n')
        return syntaxR(
          'L’indice',
          'L’indice de la somme n’est pas n : écris « Σ(k = 1 ; n) ».',
          offset + i,
        );
      if ([...symbolsOf(body)].some((s) => s !== k && s !== 'n' && s !== 'π'))
        return syntaxR(
          'Une lettre en trop',
          `Le terme de la somme ne dépend que de ${k} et de n.`,
          offset + j - 1,
        );
      if ([...symbolsOf(lo), ...symbolsOf(hi)].some((s) => s !== 'n'))
        return syntaxR('Les bornes', 'Les bornes de la somme ne dépendent que de n.', offset + i);
      const ph = `σ${sub(++PH)}`;
      sums.set(ph, {
        sign: m[1] as 'Σ' | 'Π',
        k,
        lo,
        hi,
        body,
        key: `${m[1]}|${keyOf(lo)}|${keyOf(hi)}|${keyOf(substitute(body, { [k]: { k: 'sym', n: 'κ' } }))}`,
      });
      outer.push(ph);
      outerSrc.push(i);
      i = j - 1;
      continue;
    }
    if (isIntegralAtom(a)) {
      let j = i + 1;
      while (j < A.length && !isDiffAtom(A[j])) {
        if (isIntegralAtom(A[j]) || isSumAtom(A[j]) || A[j] === '[')
          return syntaxR(
            'Ferme l’intégrale',
            'Une intégrale se ferme par « dx » avant d’écrire autre chose.',
            offset + j,
          );
        j++;
      }
      if (j >= A.length) return { k: 'incomplete' };
      const body = A.slice(i + 1, j).filter(
        (x, k, arr) => !(x === '×' && (k === 0 || k === arr.length - 1)),
      );
      if (!body.length)
        return syntaxR('La fonction à intégrer', 'Écris la fonction entre ∫ et dx.', offset + j);
      const syn = checkSyntax(normalize(body));
      if (!syn.ok)
        return syntaxR(
          'Ça commence mal',
          syn.msg,
          offset + i + 1 + Math.min(syn.index, body.length - 1),
        );
      if (!syn.complete)
        return syntaxR('Ça ne se lit pas', 'La fonction n’est pas finie.', offset + j);
      const fx = exprOf(normalize(body));
      const m = /^∫\((.+) ; (.+)\)$/.exec(a)!;
      const lo = exprOfText(m[1]!);
      const hi = exprOfText(m[2]!);
      const x = /^d([a-zθ])$/.exec(A[j]!)![1]!;
      if (!fx || !lo || !hi)
        return syntaxR('Ça ne se lit pas', 'Vérifie les parenthèses.', offset + j);
      if (x === 'n' || x === 'k')
        return syntaxR(
          'La variable',
          `Dans l’intégrale, la variable n’est ni n ni l’indice : écris « dx » et une fonction de x.`,
          offset + j,
        );
      if ([...symbolsOf(fx)].some((s) => s !== x && s !== 'π'))
        return syntaxR(
          'Une lettre en trop',
          `La fonction à intégrer ne dépend que de ${x} : remplace k ÷ n par ${x}.`,
          offset + j - 1,
        );
      if ([...symbolsOf(lo), ...symbolsOf(hi)].some((s) => s !== 'π'))
        return syntaxR('Les bornes', 'Les bornes de l’intégrale sont des nombres.', offset + i);
      const ph = `φ${sub(++PH)}`;
      ints.set(ph, {
        lo,
        hi,
        a: val(lo),
        b: val(hi),
        x,
        body: fx,
        text: kintText(A.slice(i, j + 1)),
      });
      outer.push(ph);
      outerSrc.push(i);
      i = j;
      continue;
    }
    if (a === '[' || isBracketClose(a))
      return syntaxR(
        'Un crochet à part',
        'Calcule l’intégrale sur sa propre ligne : « ∫(a ; b) … dx = [ … ](a ; b) ».',
        offset + i,
      );
    if (isDiffAtom(a))
      return syntaxR(
        '« dx » ferme une intégrale',
        'Écris d’abord « ∫(a ; b) », puis la fonction.',
        offset + i,
      );
    outer.push(a);
    outerSrc.push(i);
  }
  const O = normalize(outer);
  if (!O.length) return { k: 'incomplete' };
  const syn = checkSyntax(O, { chains: true });
  if (!syn.ok)
    return syntaxR(
      'Ça commence mal',
      syn.msg,
      offset + (outerSrc[Math.min(syn.index, outerSrc.length - 1)] ?? 0),
    );
  if (!syn.complete) return { k: 'incomplete' };
  const parts: string[][] = [[]];
  let depth = 0;
  for (const a of O) {
    if (a === '(') depth++;
    if (a === ')') depth--;
    if (depth === 0 && a === '=') parts.push([]);
    else parts[parts.length - 1]!.push(a);
  }
  const members: RForm[] = [];
  for (const p of parts) {
    if (!p.length) return { k: 'incomplete' };
    const t = exprOf(p);
    if (!t)
      return syntaxR(
        'Ça ne se lit pas',
        'Un membre ne se lit pas : vérifie les parenthèses.',
        offset + A.length - 1,
      );
    const used = symbolsOf(t);
    const ms = new Map([...sums].filter(([ph]) => used.has(ph)));
    const mi = new Map([...ints].filter(([ph]) => used.has(ph)));
    members.push({ outer: t, sums: ms, ints: mi, wkey: formKey(t, ms, mi) });
  }
  return { k: 'ok', members };
}

/** l'écriture d'une forme : l'expression, chaque somme et chaque intégrale par sa clé */
function formKey(t: Term, sums: Map<string, SumAtom>, ints: Map<string, IntAtom>): string {
  const map: Record<string, Term> = {};
  for (const [ph, s] of sums) map[ph] = { k: 'sym', n: `«${s.key}»` };
  for (const [ph, i] of ints) map[ph] = { k: 'sym', n: `«${i.text}»` };
  return shapeOf(substitute(t, map));
}

type Read =
  | { k: 'empty' }
  | { k: 'incomplete' }
  | { k: 'syntax'; title: string; message: string; index: number }
  | { k: 'noeq' }
  | { k: 'eq'; members: RForm[] }
  | { k: 'lim'; left: RForm; right: RForm };

function readLine(A: string[]): Read {
  if (!A.length) return { k: 'empty' };
  const arrows = A.map((a, i) => (a === ARROW ? i : -1)).filter((i) => i >= 0);
  if (arrows.length > 1)
    return syntaxR('Une limite par ligne', 'Écris une seule flèche « → » par ligne.', arrows[1]!);
  if (arrows.length === 1) {
    const i = arrows[0]!;
    const L = A.slice(0, i);
    const R = A.slice(i + 1);
    if (!L.length) return syntaxR('La suite', 'Avant « → », écris la suite (« Sₙ → … »).', i);
    if (L.includes('=') || R.includes('='))
      return syntaxR(
        'Une relation par ligne',
        'Une ligne est une égalité, ou une limite « Sₙ → … », pas les deux.',
        i,
      );
    const l = readSide(L, 0);
    if (l.k !== 'ok') return l;
    if (!R.length) return { k: 'incomplete' };
    const r = readSide(R, i + 1);
    if (r.k !== 'ok') return r;
    return { k: 'lim', left: l.members[0]!, right: r.members[0]! };
  }
  const s = readSide(A, 0);
  if (s.k !== 'ok') return s;
  if (s.members.length < 2) return { k: 'noeq' };
  return { k: 'eq', members: s.members };
}

/* ------------------------------ sommes de Riemann ------------------------------ */

/** t = C × ph (ph une seule fois, hors d'une fonction) : C ; null sinon */
function linearIn(t: Term, ph: string): Term | null {
  const has = (u: Term) => symbolsOf(u).has(ph);
  if (!has(t)) return null;
  switch (t.k) {
    case 'sym':
      return t.n === ph ? num(1) : null;
    case 'mul': {
      const vary = t.c.filter(has);
      if (vary.length !== 1) return null;
      const inner = linearIn(vary[0]!, ph);
      if (!inner) return null;
      const rest = t.c.filter((c) => !has(c));
      return rest.length ? { k: 'mul', c: [...rest, inner] } : inner;
    }
    case 'div': {
      if (has(t.b)) return null;
      const inner = linearIn(t.a, ph);
      return inner ? { k: 'div', a: inner, b: t.b } : null;
    }
    default:
      return null;
  }
}

/** les bornes, affines en n : k va de A·n + α à B·n + β */
function affineBounds(s: SumAtom): { A: number; B: number } | null {
  const fit = (t: Term): { s: number; c: number } | null => {
    const v1 = val(t, { n: 1 });
    const v2 = val(t, { n: 2 });
    if (!Number.isFinite(v1) || !Number.isFinite(v2)) return null;
    const sl = v2 - v1;
    const c = v1 - sl;
    if (Math.abs(sl - Math.round(sl)) > 1e-9 || Math.abs(c - Math.round(c)) > 1e-9) return null;
    for (const n of NS) if (!near(val(t, { n }), sl * n + c)) return null;
    return { s: Math.round(sl), c: Math.round(c) };
  };
  const lo = fit(s.lo);
  const hi = fit(s.hi);
  if (!lo || !hi || hi.s <= lo.s || lo.s < 0) return null;
  return { A: lo.s, B: hi.s };
}

const RATIOS = [0.061, 0.137, 0.229, 0.318, 0.411, 0.503, 0.597, 0.689, 0.773, 0.857, 0.941];
const pointsOf = (a: number, b: number): number[] => RATIOS.map((r) => a + r * (b - a));

/** h(x) = scale(n) × body(k = x·n, n) ne dépend pas de n : la fonction g */
function kOverN(
  body: Term,
  k: string,
  a: number,
  b: number,
  scale: (n: number) => number,
): ((x: number) => number) | null {
  const at = (x: number, n: number) => scale(n) * val(body, { [k]: x * n, n });
  let ok = 0;
  for (const x of pointsOf(a, b)) {
    const ref = at(x, 19);
    for (const n of [7, 53, 211]) {
      const v = at(x, n);
      if (Number.isNaN(ref) && Number.isNaN(v)) continue;
      if (!near(v, ref, 1e-8)) return null;
    }
    if (Number.isFinite(ref)) ok++;
  }
  if (ok < 6) return null;
  return (x: number) => at(x, 19);
}

/** le terme a-t-il un facteur 1 ÷ n écrit (« f(k ÷ n) ÷ n », « (1 ÷ n) × f(k ÷ n) ») ? */
function stripInvN(t: Term): Term | null {
  const isN = (u: Term) => u.k === 'sym' && u.n === 'n';
  const isInvN = (u: Term) =>
    u.k === 'div' && u.a.k === 'num' && u.a.v.n === 1n && u.a.v.d === 1n && isN(u.b);
  if (t.k === 'div' && isN(t.b)) return t.a;
  if (t.k === 'mul') {
    const i = t.c.findIndex(isInvN);
    if (i >= 0) {
      const rest = t.c.filter((_, j) => j !== i);
      return rest.length === 1 ? rest[0]! : { k: 'mul', c: rest };
    }
  }
  return null;
}

/**
 * Une somme de Riemann ÉCRITE : (λ ÷ n) × Σ f(k ÷ n) — le facteur λ ÷ n devant la somme (ou
 * 1 ÷ n dans le terme), le terme fonction de k ÷ n seulement — sur k ÷ n allant de a à b.
 */
function riemOf(f: RForm): Riem | null {
  if (f.sums.size !== 1 || f.ints.size) return null;
  const [ph, s] = [...f.sums][0]!;
  if (s.sign !== 'Σ') return null;
  const C = linearIn(f.outer, ph);
  if (!C || [...symbolsOf(C)].some((x) => x !== 'n' && x !== 'π')) return null;
  const bnd = affineBounds(s);
  if (!bnd) return null;
  const { A, B } = bnd;
  // le facteur devant la somme : λ ÷ n
  const ln = [3, 7, 20].map((n) => val(C, { n }) * n);
  if (ln.every((v) => near(v, ln[0]!)) && !near(ln[0]!, 0)) {
    const g = kOverN(s.body, s.k, A, B, () => 1);
    return g ? { lambda: ln[0]!, a: A, b: B, g } : null;
  }
  // 1 ÷ n dans le terme, une constante devant
  const c0 = [3, 7, 20].map((n) => val(C, { n }));
  if (c0.every((v) => near(v, c0[0]!)) && !near(c0[0]!, 0)) {
    const inner = stripInvN(s.body);
    if (!inner) return null;
    const g = kOverN(inner, s.k, A, B, () => 1);
    return g ? { lambda: c0[0]!, a: A, b: B, g } : null;
  }
  return null;
}

/** la même somme lue comme une somme de Riemann, sans exiger l'écriture (n × C(n) × terme) */
function semRiemOf(f: RForm): Riem | null {
  if (f.sums.size !== 1 || f.ints.size) return null;
  const [ph, s] = [...f.sums][0]!;
  if (s.sign !== 'Σ') return null;
  const C = linearIn(f.outer, ph);
  if (!C || [...symbolsOf(C)].some((x) => x !== 'n' && x !== 'π')) return null;
  const bnd = affineBounds(s);
  if (!bnd) return null;
  const g = kOverN(s.body, s.k, bnd.A, bnd.B, (n) => n * val(C, { n }));
  return g ? { lambda: 1, a: bnd.A, b: bnd.B, g } : null;
}

const riemValue = (r: Riem): number => r.lambda * integrateNum(r.g, r.a, r.b);

/* ------------------------------ formes ------------------------------ */

const prodCount = (f: RForm) => [...f.sums.values()].filter((s) => s.sign === 'Π').length;
const sumCount = (f: RForm) => [...f.sums.values()].filter((s) => s.sign === 'Σ').length;

/** ln d'une puissance dont la base contient un produit Π */
function lnPow(f: RForm): boolean {
  const prods = new Set([...f.sums].filter(([, s]) => s.sign === 'Π').map(([ph]) => ph));
  let found = false;
  const walk = (t: Term) => {
    if (found) return;
    if (t.k === 'fn') {
      if (t.f === 'ln' && t.a.k === 'pow' && [...symbolsOf(t.a.a)].some((s) => prods.has(s)))
        found = true;
      walk(t.a);
    } else if (t.k === 'add' || t.k === 'mul') t.c.forEach(walk);
    else if (t.k === 'div' || t.k === 'pow' || t.k === 'binom') {
      walk(t.a);
      walk(t.b);
    }
  };
  walk(f.outer);
  return found;
}

const boundsKey = (f: RForm): string =>
  [...f.sums.values()]
    .filter((s) => s.sign === 'Σ')
    .map((s) => `${keyOf(s.lo)}|${keyOf(s.hi)}`)
    .sort()
    .join(',');

type G = { k: 'step'; steps: StepKind[] } | { k: 'same' } | { k: 'flat' };

/** le passage de l'écriture F à l'écriture R (de même valeur) */
function gesture(F: RForm, R: RForm): G {
  if (F.wkey === R.wkey) return { k: 'same' };
  const steps: StepKind[] = [];
  const toSum = prodCount(F) > prodCount(R) && sumCount(R) > sumCount(F);
  if (toSum) {
    if (lnPow(F)) steps.push('logPower');
    steps.push('logProduct');
  } else if (lnPow(F) && !lnPow(R)) steps.push('logPower');
  if (sumCount(F) && sumCount(R) && boundsKey(F) !== boundsKey(R)) steps.push('reindex');
  if (sumCount(F) && !toSum && !riemOf(F) && riemOf(R)) steps.push('rewrite');
  return steps.length ? { k: 'step', steps } : { k: 'flat' };
}

/* ------------------------------ état ------------------------------ */

const FORMAT = (v: number): string => {
  if (!Number.isFinite(v)) return '?';
  const r = Math.round(v * 1e4) / 1e4;
  return String(r).replace('.', ',').replace('-', '−');
};

/** l'expression de la suite (« Sₙ », « ln(Pₙ) ») : sa clé */
const subjectKey = (t: Term): string => keyOf(t);

export function riemannQuestionOf(model: ScratchModel): {
  X: string;
  start: RForm;
  answer: Term;
} {
  const spec = model.kernel!;
  const X = spec.goal.target;
  const A = kriemLineAtoms(spec.start);
  if (A[0] !== X || A[1] !== '=')
    throw new RiemannModelError(`Le départ s’écrit « ${X} = Σ(k = 1 ; n) … ».`);
  const r = readSide(A.slice(2), 2);
  if (r.k !== 'ok' || r.members.length !== 1)
    throw new RiemannModelError(`Départ illisible : « ${spec.start} ».`);
  const start = r.members[0]!;
  if (!start.sums.size || start.ints.size)
    throw new RiemannModelError('Le départ est une somme (ou un produit), sans intégrale.');
  if ([...symbolsOf(start.outer)].some((s) => s !== 'n' && s !== 'π' && !start.sums.has(s)))
    throw new RiemannModelError('Le départ ne dépend que de n.');
  const ans = kriemLineAtoms(spec.answer);
  const ai = ans.indexOf(ARROW);
  if (ai < 0 || ans[0] !== X || ai !== 1)
    throw new RiemannModelError(`La réponse s’écrit « ${X} → valeur ».`);
  const answer = exprOf(ans.slice(ai + 1));
  if (!answer || [...symbolsOf(answer)].some((s) => s !== 'π'))
    throw new RiemannModelError('La réponse est une valeur (des nombres, e, π, ln…).');
  return { X, start, answer };
}

export function createKRiemState(model: ScratchModel): KRiemState {
  const spec = model.kernel!;
  const q = riemannQuestionOf(model);
  const Xt: Term = { k: 'sym', n: q.X };
  const st: KRiemState = {
    model,
    X: q.X,
    start: q.start,
    startText: kriemText(kriemLineAtoms(spec.start)),
    answer: q.answer,
    answerValue: val(q.answer),
    answerOps: opsOf(q.answer),
    forms: new Map([[subjectKey(Xt), { subject: Xt, forms: [q.start] }]]),
    lims: new Map(),
    subs: [],
    written: new Set(),
    givenTexts: new Set(),
    path: spec.path ?? [],
    intPath: [],
    traps: [],
    lines: 0,
    concluded: false,
  };
  if (!Number.isFinite(st.answerValue))
    throw new RiemannModelError('La réponse n’a pas de valeur.');
  st.givenTexts = new Set(riemannGivenTexts(model).map((t) => kriemText(kriemLineAtoms(t))));
  st.intPath = st.path.filter((t) => isIntegralLine(kriemLineAtoms(t)));
  st.traps = (model.traps ?? []).map((t) => ({
    text: kriemText(kriemLineAtoms(t.line)),
    line: kriemLine(t.line),
    title: t.title ?? 'Attention',
    why: t.why,
  }));
  return st;
}

/** les lignes de l'énoncé : la suite et sa définition */
export function riemannGivenTexts(model: ScratchModel): string[] {
  return [model.kernel!.start, ...(model.givens ?? [])];
}

/** la valeur de la suite (X) pour n */
const defValue = (st: KRiemState, n: number): number => formValue(st.start, n);

function subjectValue(st: KRiemState, L: Term, n: number): number {
  const x = defValue(st, n);
  if (!Number.isFinite(x)) return NaN;
  return val(L, { [st.X]: x, n });
}
/** la vraie limite d'une expression de la suite (la limite de X est la réponse) */
const truthOf = (st: KRiemState, L: Term): number => val(L, { [st.X]: st.answerValue });

/** une expression de la suite valable à gauche : « Sₙ », « ln(Pₙ) » */
function subjectOk(st: KRiemState, f: RForm): boolean {
  if (f.sums.size || f.ints.size) return false;
  const s = symbolsOf(f.outer);
  return s.has(st.X) && [...s].every((x) => x === st.X || x === 'π');
}

/** les écritures connues d'une expression de la suite (sinon : X remplacé par sa définition) */
function formsOf(st: KRiemState, L: Term): RForm[] {
  const known = st.forms.get(subjectKey(L));
  if (known) return [...known.forms].reverse();
  const out: RForm[] = [];
  const x = st.forms.get(subjectKey({ k: 'sym', n: st.X }))!;
  for (const F of [...x.forms].reverse()) {
    // L(X) avec X remplacé par une somme : on garde les sommes de F
    const outer = substitute(L, { [st.X]: F.outer });
    out.push({ outer, sums: F.sums, ints: F.ints, wkey: formKey(outer, F.sums, F.ints) });
  }
  return out;
}

/* ------------------------------ verdicts ------------------------------ */

type Base = { atoms: string[]; line: Line };

function stepVerdict(base: Base, steps: StepKind[], move: RMove, final: boolean): KRiemVerdict {
  const uniq = [...new Set(steps)];
  const hora = uniq.reduce((s, k) => s + STEP[k].hora, 0);
  let main = uniq[0]!;
  for (const k of uniq) if (STEP[k].hora > STEP[main].hora) main = k;
  const label =
    uniq.includes('logPower') && uniq.includes('logProduct')
      ? [
          'Passer au logarithme',
          ...uniq.filter((k) => k !== 'logPower' && k !== 'logProduct').map((k) => STEP[k].label),
        ].join(' → ')
      : uniq.map((k) => STEP[k].label).join(' → ');
  const why = uniq.map((k) => STEP[k].why).join(' ') + (final ? ' C’est la réponse.' : '');
  return {
    kind: 'step',
    ...base,
    hora,
    final,
    rule: { id: RIEMANN_STEP_IDS[main], card: CARD, label, hora, why },
    rmove: move,
  };
}

function jumpVerdict(
  base: Base,
  move: RMove | undefined,
  final: boolean,
  message?: string,
): KRiemVerdict {
  return {
    kind: 'useless',
    ...base,
    jump: true,
    final,
    title: final ? 'Juste !' : 'Juste, mais tu sautes des étapes',
    message:
      message ??
      (final
        ? 'C’est la bonne limite. Détaille ton calcul, une étape par ligne, pour gagner des HORA. Tu peux garder cette ligne sans HORA.'
        : 'Ta ligne est juste. Détaille-la, une étape à la fois, pour gagner des HORA. Tu peux la garder sans HORA.'),
    ...(move ? { rmove: move } : {}),
  };
}

function trapOr(st: KRiemState, base: Base, why: { title: string; message: string }): KRiemVerdict {
  const text = kriemText(base.atoms);
  const t = st.traps.find((t) => t.text === text);
  if (t)
    return {
      kind: 'trap',
      ...base,
      title: t.title,
      message: t.why,
      trap: { line: t.line, title: t.title, why: t.why },
    };
  return { kind: 'false', ...base, ...why };
}

/* ------------------------------ égalités ------------------------------ */

function subjectName(st: KRiemState, L: Term): string {
  return L.k === 'sym' ? L.n : `cette expression de ${st.X}`;
}

function diagnoseEq(st: KRiemState, L: Term, R: RForm): { title: string; message: string } {
  const lv = (n: number) => subjectValue(st, L, n);
  const rv = (n: number) => formValue(R, n);
  const ratio = NS.map((n) => rv(n) / lv(n)).filter((v) => Number.isFinite(v));
  const name = subjectName(st, L);
  if (ratio.length >= 6) {
    const byN = NS.map((n) => rv(n) / lv(n) / n).filter((v) => Number.isFinite(v));
    const timesN = NS.map((n) => (rv(n) / lv(n)) * n).filter((v) => Number.isFinite(v));
    if (byN.length >= 6 && byN.every((v) => near(v, 1, 1e-7)))
      return {
        title: 'Un facteur n de trop',
        message: `Ton écriture vaut n fois ${name} : il y a un facteur n de trop.`,
      };
    if (timesN.length >= 6 && timesN.every((v) => near(v, 1, 1e-7)))
      return {
        title: 'Il manque un facteur n',
        message: `Ton écriture vaut ${name} ÷ n : il manque un facteur n (si tu sors 1 ÷ n de la somme, multiplie ce qu’il reste par n).`,
      };
    if (ratio.every((v) => near(v, ratio[0]!, 1e-7)) && !near(ratio[0]!, 1))
      return {
        title: 'Un facteur ne va pas',
        message: `Ton écriture vaut ${FORMAT(ratio[0]!)} fois ${name}.`,
      };
  }
  return {
    title: 'Ce n’est pas égal',
    message: `Pour n = 3, ${name} vaut ${FORMAT(lv(3))} et ton écriture ${FORMAT(rv(3))}.`,
  };
}

function judgeEq(st: KRiemState, base: Base, members: RForm[]): KRiemVerdict {
  const text = kriemText(base.atoms);
  if (st.givenTexts.has(text))
    return {
      kind: 'useless',
      ...base,
      title: 'C’est l’énoncé',
      message: 'Cette ligne est déjà dans l’énoncé.',
    };
  const left = members[0]!;
  if (!subjectOk(st, left))
    return {
      kind: 'unjustified',
      ...base,
      title: `Pars de ${st.X}`,
      message: `Écris « ${st.X} = … » (ou « ln(${st.X}) = … »).`,
    };
  const L = left.outer;
  for (const R of members.slice(1)) {
    if (R.ints.size)
      return {
        kind: 'false',
        ...base,
        title: 'Une limite, pas une égalité',
        message: `${subjectName(st, L)} n’est pas égale à une intégrale : elle tend vers elle. Écris « ${st.X} → ∫(…) … dx ».`,
      };
    if (symbolsOf(R.outer).has(st.X))
      return {
        kind: 'unjustified',
        ...base,
        title: 'Une écriture de la suite',
        message: `À droite du « = », écris la suite avec n (une somme, un produit), sans ${st.X}.`,
      };
    if ([...symbolsOf(R.outer)].some((x) => x !== 'n' && x !== 'π' && !R.sums.has(x)))
      return {
        kind: 'syntax',
        ...base,
        title: 'Une lettre en trop',
        message: 'Hors de la somme, l’écriture ne dépend que de n.',
      };
  }
  // vrai ?
  for (let i = 1; i < members.length; i++) {
    const R = members[i]!;
    if (
      !sameSeq(
        (n) => subjectValue(st, L, n),
        (n) => formValue(R, n),
      )
    )
      return trapOr(st, base, diagnoseEq(st, L, R));
  }
  const skey = subjectKey(L);
  const move: RMove = { k: 'eq', skey, subject: L, forms: members.slice(1), text };
  const steps: StepKind[] = [];
  let same = 0;
  let flat = 0;
  for (let i = 1; i < members.length; i++) {
    const T = members[i]!;
    let g: G | null = null;
    if (i === 1) {
      for (const S of formsOf(st, L)) {
        const h = gesture(S, T);
        if (h.k === 'step') {
          g = h;
          break;
        }
        if (!g || h.k === 'same') g = h;
      }
    } else g = gesture(members[i - 1]!, T);
    if (!g) continue;
    if (g.k === 'step') steps.push(...g.steps);
    else if (g.k === 'same') same++;
    else flat++;
  }
  if (!steps.length)
    return {
      kind: 'useless',
      ...base,
      title: same && !flat ? 'Rien de nouveau' : 'Juste, mais ça ne t’avance pas',
      message:
        same && !flat
          ? 'Cette écriture est déjà sur ta feuille.'
          : 'Les deux écritures sont égales, mais le calcul n’avance pas : vise (1 ÷ n) × Σ f(k ÷ n).',
      rmove: move,
    };
  return stepVerdict(base, steps, move, false);
}

/* ------------------------------ limites ------------------------------ */

/** l'intégrale attendue par une somme de Riemann établie : (a, b, g), au coefficient près */
function intMatches(I: IntAtom, coef: number, r: Riem): 'yes' | 'bounds' | 'fn' | 'coef' | 'no' {
  const sameBounds = near(I.a, r.a) && near(I.b, r.b);
  const f = (x: number) => coef * val(I.body, { [I.x]: x });
  const fx = pointsOf(r.a, r.b);
  const eq = (h: (x: number) => number) => {
    let ok = 0;
    for (const x of fx) {
      const u = h(x);
      const v = r.lambda * r.g(x);
      if (Number.isNaN(u) && Number.isNaN(v)) continue;
      if (!near(u, v, 1e-8)) return false;
      ok++;
    }
    return ok >= 6;
  };
  const sameFn = eq(f);
  if (sameBounds && sameFn) return 'yes';
  if (sameFn) return 'bounds';
  if (sameBounds) {
    // le même au facteur près ?
    const ratios = fx.map((x) => f(x) / (r.lambda * r.g(x))).filter((v) => Number.isFinite(v));
    if (ratios.length >= 6 && ratios.every((v) => near(v, ratios[0]!, 1e-8))) return 'coef';
    return 'fn';
  }
  return 'no';
}

function boundsText(r: Riem): string {
  return `k ÷ n va de ${FORMAT(r.a)} à ${FORMAT(r.b)}`;
}

function judgeLimIntegral(st: KRiemState, base: Base, L: Term, R: RForm): KRiemVerdict {
  const text = kriemText(base.atoms);
  if (R.ints.size !== 1 || R.sums.size)
    return {
      kind: 'syntax',
      ...base,
      title: 'Une intégrale',
      message:
        'À droite de la flèche, écris une intégrale (éventuellement multipliée par un nombre).',
    };
  const [ph, I] = [...R.ints][0]!;
  const C = linearIn(R.outer, ph);
  if (!C || [...symbolsOf(C)].some((x) => x !== 'π'))
    return {
      kind: 'syntax',
      ...base,
      title: 'Une intégrale',
      message: 'L’intégrale se multiplie par un nombre ; elle ne se met pas dans une fonction.',
    };
  const coef = val(C);
  const value = coef * integrateNum((x) => val(I.body, { [I.x]: x }), I.a, I.b);
  const truth = truthOf(st, L);
  const forms = formsOf(st, L);
  const riems = forms.map(riemOf).filter((r): r is Riem => !!r);
  const sems = forms.map(semRiemOf).filter((r): r is Riem => !!r);
  if (!near(value, truth, 1e-7)) {
    for (const r of [...riems, ...sems]) {
      const m = intMatches(I, coef, r);
      if (m === 'bounds')
        return trapOr(st, base, {
          title: 'Les bornes',
          message: `La fonction est la bonne, pas les bornes : ${boundsText(r)}.`,
        });
      if (m === 'fn')
        return trapOr(st, base, {
          title: 'Pas la bonne fonction',
          message:
            'Les bornes vont ; pour la fonction, remplace k ÷ n par x dans ce qu’on additionne.',
        });
      if (m === 'coef')
        return trapOr(st, base, {
          title: 'Le coefficient',
          message:
            'La fonction et les bornes vont, pas le nombre devant : regarde le facteur devant la somme.',
        });
    }
    return trapOr(st, base, {
      title: 'Ce n’est pas la limite',
      message: riems.length
        ? 'Remplace k ÷ n par x dans ce qu’on additionne, et regarde les valeurs de k ÷ n pour les bornes.'
        : 'Fais d’abord apparaître une somme de Riemann : (1 ÷ n) × Σ f(k ÷ n).',
    });
  }
  const move: RMove = {
    k: 'riem',
    skey: subjectKey(L),
    subject: L,
    value,
    int: I.text,
    coef,
    text,
  };
  const lim = st.lims.get(subjectKey(L));
  if (lim?.ints.some((i) => st.subs[i.sub]?.text === I.text && near(i.coef, coef)))
    return {
      kind: 'useless',
      ...base,
      title: 'Rien de nouveau',
      message: 'Cette limite est déjà sur ta feuille.',
    };
  if (riems.some((r) => intMatches(I, coef, r) === 'yes'))
    return stepVerdict(base, ['riemann'], move, false);
  if (sems.some((r) => intMatches(I, coef, r) === 'yes'))
    return jumpVerdict(
      base,
      move,
      false,
      `Juste, mais fais d’abord apparaître la somme de Riemann : écris « ${st.X} = (1 ÷ n) × Σ… » avec ce qu’on additionne fonction de k ÷ n.`,
    );
  return jumpVerdict(
    base,
    move,
    false,
    'Juste, mais écris l’intégrale qui vient de la somme : remplace k ÷ n par x.',
  );
}

function judgeLimValue(st: KRiemState, base: Base, L: Term, R: RForm): KRiemVerdict {
  const text = kriemText(base.atoms);
  if (R.sums.size || symbolsOf(R.outer).has('n') || symbolsOf(R.outer).has(st.X))
    return {
      kind: 'false',
      ...base,
      title: 'Une limite ne dépend pas de n',
      message: 'À droite de la flèche, écris un nombre (ou une intégrale), sans n.',
    };
  const t = R.outer;
  const v = val(t);
  const truth = truthOf(st, L);
  const skey = subjectKey(L);
  const isX = L.k === 'sym' && L.n === st.X;
  const final = isX && near(v, st.answerValue) && opsOf(t) <= st.answerOps;
  if (!near(v, truth, 1e-9)) {
    const lim = st.lims.get(skey);
    if (lim?.ints.length) {
      const i = lim.ints[0]!;
      const sv = st.subs[i.sub]?.value ?? NaN;
      if (near(v, sv) && !near(i.coef, 1))
        return trapOr(st, base, {
          title: 'Le coefficient',
          message: `${subjectName(st, L)} tend vers ${FORMAT(i.coef)} fois l’intégrale : n’oublie pas le nombre devant.`,
        });
    }
    return trapOr(st, base, {
      title: 'Ce n’est pas la limite',
      message: 'Ce nombre n’est pas la limite : calcule l’intégrale, puis conclus.',
    });
  }
  const move: RMove = { k: 'val', skey, subject: L, value: v, t, text, final };
  const lim = st.lims.get(skey);
  // déjà des écritures de la limite : calculer
  if (lim?.writings.length) {
    const wk = shapeOf(t);
    if (lim.writings.some((w) => w.wkey === wk))
      return {
        kind: 'useless',
        ...base,
        title: 'Rien de nouveau',
        message: 'Cette limite est déjà sur ta feuille.',
      };
    const o = opsOf(t);
    const best = Math.min(...lim.writings.map((w) => w.ops));
    if (o < best) {
      if (best - o > 4) return jumpVerdict(base, move, final);
      return stepVerdict(base, ['compute'], move, final);
    }
    return {
      kind: 'useless',
      ...base,
      title: 'Juste, mais ça ne t’avance pas',
      message: 'Les deux écritures de la limite sont égales, mais le calcul n’avance pas.',
      rmove: move,
      final,
    };
  }
  // la suite tend vers une intégrale : conclure quand l'intégrale est calculée
  if (lim?.ints.length) {
    const computed = lim.ints.some((i) => st.subs[i.sub] && kintStartComputed(st.subs[i.sub]!.ks));
    if (computed) return stepVerdict(base, ['conclude'], move, final);
    return jumpVerdict(
      base,
      move,
      final,
      'Juste, mais calcule d’abord l’intégrale sur ta feuille : « ∫(…) … dx = … ».',
    );
  }
  // composer par exp : ln(X) → ℓ établi, X → exp(ℓ)
  for (const [, M] of st.lims) {
    if (!M.writings.length) continue;
    const isExpOf = sameSeq(
      (n) => subjectValue(st, L, n),
      (n) => Math.exp(subjectValue(st, M.subject, n)),
    );
    if (!isExpOf) continue;
    const isExpForm = t.k === 'fn' && t.f === 'exp' && near(val(t.a), M.value);
    if (isExpForm) return stepVerdict(base, ['compose'], move, final);
    return jumpVerdict(
      base,
      move,
      final,
      'Juste, mais une étape à la fois : compose d’abord par exp, « … → e^(…) », puis calcule.',
    );
  }
  return jumpVerdict(base, move, final);
}

/* ------------------------------ intégrales ------------------------------ */

/** une ligne de calcul d'intégrale : « ∫(…) … dx = … », « [ … ](…) = … », « u(x) = … » */
function isIntegralLine(A: string[]): boolean {
  return !!A.length && (isIntegralAtom(A[0]) || A[0] === '[' || NAME_RE.test(A[0]!));
}

const fmtDec = (v: number): string => {
  const s = v.toFixed(12).replace(/0+$/, '').replace(/\.$/, '');
  return s.replace('.', ',').replace('-', '−');
};

/** la feuille d'intégrale d'une intégrale de la feuille */
function makeSub(st: KRiemState, text: string): Sub {
  const A = kriemLineAtoms(text);
  const r = readSide(A, 0);
  if (r.k !== 'ok' || r.members.length !== 1 || r.members[0]!.ints.size !== 1)
    throw new RiemannModelError(`Intégrale illisible : « ${text} ».`);
  const I = [...r.members[0]!.ints.values()][0]!;
  const value = integrateNum((x) => val(I.body, { [I.x]: x }), I.a, I.b);
  const model: ScratchModel = {
    ...st.model,
    symbols: { I: {} },
    goal: ['I'],
    traps: [],
    choices: undefined,
    givens: undefined,
    kernel: {
      plugin: 'integral',
      goal: { kind: 'integrate', target: 'I' },
      start: `I = ${I.text}`,
      answer: `I = ${fmtDec(value)}`,
      path: [`I = ${I.text}`],
    },
  } as unknown as ScratchModel;
  const out: Sub = { ks: createKIntState(model), text: I.text, value };
  // le corrigé de l'intégrale, lu par la sous-feuille (« I = … »)
  out.ks.path = st.intPath.map((t) => subText(out, t));
  return out;
}

/** l'intégrale par laquelle commence une ligne (son écriture) */
function leadIntegral(A: string[]): string | null {
  if (!isIntegralAtom(A[0])) return null;
  const j = A.findIndex(isDiffAtom);
  return j > 0 ? kintText(A.slice(0, j + 1)) : null;
}

/** une intégrale que la feuille attend : celle d'une somme de Riemann établie */
function expectedIntegral(st: KRiemState, text: string): boolean {
  const A = kriemLineAtoms(text);
  const r = readSide(A, 0);
  if (r.k !== 'ok' || r.members.length !== 1 || r.members[0]!.ints.size !== 1) return false;
  const I = [...r.members[0]!.ints.values()][0]!;
  for (const { forms } of st.forms.values())
    for (const F of forms) {
      const rr = riemOf(F) ?? semRiemOf(F);
      if (!rr) continue;
      const m = intMatches(I, 1, rr);
      if (m === 'yes') return true;
      if (m === 'coef' || m === 'fn') {
        // λ × ∫ g : l'intégrale de g seule
        const g = { ...rr, lambda: 1 };
        if (intMatches(I, 1, g) === 'yes') return true;
      }
    }
  return false;
}

const RANK: Record<KIntVerdict['kind'], number> = {
  step: 10,
  ready: 9,
  useless: 7,
  building: 6,
  pending: 5,
  open: 4,
  unjustified: 3,
  trap: 2,
  false: 1,
  syntax: 0,
  empty: -1,
};
const scoreOf = (v: KIntVerdict): number =>
  RANK[v.kind] * 100 + (v.hora ?? 0) + (v.kind === 'useless' ? (v.jump ? 20 : 10) : 0);

/** une ligne d'une intégrale que la sous-feuille ne connaît pas */
const orphan = (v: KIntVerdict): boolean =>
  v.kind === 'unjustified' && (v.title === 'Pas dans ton calcul' || v.title === 'Pars de I');

/**
 * La ligne lue par la feuille d'intégrale : l'intégrale de départ, en tête, devient « I »
 * (la sous-feuille calcule I) ; `shift` : nombre de bouts retirés.
 */
function subLine(sub: Sub, A: string[]): { atoms: string[]; shift: number } {
  const lead = leadIntegral(A);
  if (lead !== sub.text) return { atoms: A, shift: 0 };
  const j = A.findIndex(isDiffAtom);
  return { atoms: ['I', ...A.slice(j + 1)], shift: j };
}
const subText = (sub: Sub, text: string): string => {
  try {
    const { atoms } = subLine(sub, kriemLineAtoms(text));
    return kintText(atoms);
  } catch {
    return text;
  }
};

function wrapInt(
  v: KIntVerdict,
  A: string[],
  shift: number,
  badChip: (i: number) => number,
  sub: number,
  fresh?: string,
): KRiemVerdict {
  const out: KRiemVerdict = {
    ...(v as unknown as KRiemVerdict),
    atoms: A,
    final: false,
  };
  delete (out as { imove?: unknown }).imove;
  if (v.line) out.line = lineOf(A);
  if (v.badChip !== undefined && v.badChip >= 0)
    out.badChip = badChip(v.badChip === 0 ? 0 : v.badChip + shift);
  if (out.rule) out.rule = { ...out.rule, why: out.rule.why.replace(' C’est la réponse.', '') };
  if (v.kind === 'step' || v.kind === 'useless' || v.kind === 'ready')
    out.rmove = { k: 'int', sub, ...(fresh ? { fresh } : {}), v, text: kriemText(A) };
  return out;
}

/**
 * La sous-feuille attend la suite d'une ligne de SON corrigé (« I = ln(2) » avant
 * « − ln(1) ») ; si cette ligne-là est déjà écrite, la ligne est jugée telle quelle.
 */
function waiting(st: KRiemState, A: string[], v: KIntVerdict, real: KIntVerdict): KIntVerdict {
  if (!(v.kind === 'ready' || (v.kind === 'building' && v.complete)) || knownPrefix(st, A))
    return v;
  if (real.kind === 'unjustified' || real.kind === 'false')
    return { ...real, kind: 'pending', complete: true };
  return real;
}

function judgeIntegral(
  st: KRiemState,
  A: string[],
  badChip: (i: number) => number,
  opts?: { verify?: boolean },
): KRiemVerdict {
  // la sous-feuille qui connaît cette intégrale
  let best: { i: number; v: KIntVerdict; shift: number } | null = null;
  for (let i = 0; i < st.subs.length; i++) {
    const sl = subLine(st.subs[i]!, A);
    const chips = chipsOf(sl.atoms);
    const real = kintJudge(st.subs[i]!.ks, chips, { verify: true });
    if (orphan(real)) continue;
    const v = opts?.verify ? real : waiting(st, A, kintJudge(st.subs[i]!.ks, chips, opts), real);
    if (!best || scoreOf(v) > scoreOf(best.v)) best = { i, v, shift: sl.shift };
  }
  if (best) return wrapInt(best.v, A, best.shift, badChip, best.i);
  // une intégrale nouvelle, attendue par une somme de Riemann : sa feuille à part
  const lead = leadIntegral(A);
  if (lead && expectedIntegral(st, lead)) {
    try {
      const s = makeSub(st, lead);
      const sl = subLine(s, A);
      const chips = chipsOf(sl.atoms);
      const v = opts?.verify
        ? kintJudge(s.ks, chips, opts)
        : waiting(st, A, kintJudge(s.ks, chips, opts), kintJudge(s.ks, chips, { verify: true }));
      return wrapInt(v, A, sl.shift, badChip, -1, lead);
    } catch {
      /* rien */
    }
  }
  const last = A[A.length - 1]!;
  const complete = A.includes('=') && (endsOp(last) || isDiffAtom(last) || isBracketClose(last));
  if (!opts?.verify && !complete) return { kind: 'open', atoms: A, complete: false };
  return {
    kind: opts?.verify ? 'unjustified' : 'pending',
    atoms: A,
    line: lineOf(A),
    complete: true,
    title: 'D’où vient cette intégrale ?',
    message: `Écris d’abord la limite de ${st.X} comme une intégrale : « ${st.X} → ∫(…) … dx ».`,
  };
}

/* ------------------------------ juge ------------------------------ */

/**
 * La ligne commence comme une ligne du corrigé PAS ENCORE écrite : elle attend la suite
 * (« ∫(0 ; 1) … dx = ln(2) » n'attend pas « − ln(1) » une fois cette ligne-là écrite).
 */
function knownPrefix(st: KRiemState, A: string[]): boolean {
  return st.path.some((t) => {
    let atoms: string[];
    try {
      atoms = kriemNormalize(kriemLineAtoms(t)).atoms;
    } catch {
      return false;
    }
    if (st.written.has(kriemText(atoms))) return false;
    return atoms.length > A.length && A.every((a, i) => atoms[i] === a);
  });
}

export function kriemJudge(
  st: KRiemState,
  chips: Chip[],
  opts?: { verify?: boolean },
): KRiemVerdict {
  const raw = chips.flatMap((c) => c.atoms);
  const chipOf: number[] = [];
  chips.forEach((c, ci) => c.atoms.forEach(() => chipOf.push(ci)));
  const norm = kriemNormalize(raw);
  const A = norm.atoms;
  if (!A.length) return { kind: 'empty', atoms: A, badChip: -1 };
  if (isIntegralLine(A))
    return judgeIntegral(
      st,
      A,
      (i) => chipOf[norm.src[i] ?? raw.length - 1] ?? chips.length - 1,
      opts,
    );
  const prefix = knownPrefix(st, A);
  const incomplete = (): KRiemVerdict =>
    opts?.verify
      ? { kind: 'syntax', atoms: A, title: 'Ligne inachevée', message: 'Termine ta ligne.' }
      : { kind: prefix ? 'building' : 'open', atoms: A, complete: false };
  const r = readLine(A);
  if (r.k === 'empty') return { kind: 'empty', atoms: A, badChip: -1 };
  if (r.k === 'incomplete') return incomplete();
  if (r.k === 'syntax') {
    if (prefix && !opts?.verify) return { kind: 'building', atoms: A, complete: false };
    return {
      kind: 'syntax',
      atoms: A,
      title: r.title,
      message: r.message,
      badChip: chipOf[norm.src[r.index] ?? raw.length - 1] ?? chips.length - 1,
    };
  }
  if (r.k === 'noeq') {
    if (opts?.verify)
      return {
        kind: 'syntax',
        atoms: A,
        line: lineOf(A),
        title: 'Une égalité ou une limite',
        message: `Écris « ${st.X} = … » ou « ${st.X} → … ».`,
      };
    return { kind: prefix ? 'building' : 'open', atoms: A, complete: false };
  }
  const base: Base = { atoms: A, line: lineOf(A) };
  let v: KRiemVerdict;
  if (r.k === 'eq') v = judgeEq(st, base, r.members);
  else if (!subjectOk(st, r.left))
    v = {
      kind: 'unjustified',
      ...base,
      title: `Pars de ${st.X}`,
      message: `Écris « ${st.X} → … » (ou « ln(${st.X}) → … »).`,
    };
  else if (r.right.ints.size) v = judgeLimIntegral(st, base, r.left.outer, r.right);
  else v = judgeLimValue(st, base, r.left.outer, r.right);
  if (prefix && !opts?.verify) {
    if (v.kind === 'step') return { ...v, kind: 'ready', complete: true };
    return { kind: 'building', atoms: A, complete: true };
  }
  if (!opts?.verify && (v.kind === 'unjustified' || v.kind === 'false'))
    return { kind: 'pending', atoms: A, complete: true };
  return { ...v, complete: true };
}

/* ------------------------------ mise à jour ------------------------------ */

export function kriemCommit(st: KRiemState, v: KRiemVerdict): void {
  const m = v.rmove;
  if (!m) return;
  st.written.add(m.text);
  st.lines++;
  if (m.k === 'int') {
    let i = m.sub;
    if (i < 0 && m.fresh) {
      const s = makeSub(st, m.fresh);
      st.subs.push(s);
      i = st.subs.length - 1;
    }
    const s = st.subs[i];
    if (s) kintCommit(s.ks, m.v);
    return;
  }
  if (m.k === 'eq') {
    const cur = st.forms.get(m.skey) ?? {
      subject: m.subject,
      forms: formsOf(st, m.subject).reverse(),
    };
    cur.forms.push(...m.forms);
    st.forms.set(m.skey, cur);
    return;
  }
  const lim: Lim = st.lims.get(m.skey) ?? {
    subject: m.subject,
    value: m.value,
    ints: [],
    writings: [],
  };
  if (m.k === 'riem') {
    let i = st.subs.findIndex((s) => s.text === m.int);
    if (i < 0) {
      try {
        st.subs.push(makeSub(st, m.int));
        i = st.subs.length - 1;
      } catch {
        i = -1;
      }
    }
    if (i >= 0) lim.ints.push({ sub: i, coef: m.coef });
  } else {
    lim.writings.push({ t: m.t, wkey: shapeOf(m.t), ops: opsOf(m.t) });
    if (m.final) st.concluded = true;
  }
  st.lims.set(m.skey, lim);
}

export function kriemGoalReached(st: KRiemState): boolean {
  return st.concluded;
}

/* ------------------------------ lignes ------------------------------ */

function readText(text: string): Read | null {
  try {
    return readLine(kriemLineAtoms(text));
  } catch {
    return null;
  }
}

/** la ligne est vraie */
export function kriemHolds(st: KRiemState, text: string): boolean {
  let A: string[];
  try {
    A = kriemLineAtoms(text);
  } catch {
    return false;
  }
  if (isIntegralLine(A)) return st.subs.some((s) => kintHolds(s.ks, subText(s, text)));
  const r = readLine(A);
  if (!r) return false;
  if (r.k === 'eq') {
    const L = r.members[0]!;
    if (!subjectOk(st, L)) return false;
    return r.members.slice(1).every(
      (R) =>
        !R.ints.size &&
        sameSeq(
          (n) => subjectValue(st, L.outer, n),
          (n) => formValue(R, n),
        ),
    );
  }
  if (r.k === 'lim') {
    if (!subjectOk(st, r.left)) return false;
    const truth = truthOf(st, r.left.outer);
    if (r.right.ints.size === 1 && !r.right.sums.size) {
      const [ph, I] = [...r.right.ints][0]!;
      const C = linearIn(r.right.outer, ph);
      if (!C) return false;
      const value = val(C) * integrateNum((x) => val(I.body, { [I.x]: x }), I.a, I.b);
      return near(value, truth, 1e-7);
    }
    if (r.right.sums.size || r.right.ints.size || symbolsOf(r.right.outer).has('n')) return false;
    return near(val(r.right.outer), truth, 1e-9);
  }
  return false;
}

/** la ligne donne la limite de la suite (un choix du QCM) */
export function kriemIsAnswer(st: KRiemState, text: string): boolean {
  const r = readText(text);
  if (!r || r.k !== 'lim') return false;
  const L = r.left.outer;
  if (!(L.k === 'sym' && L.n === st.X)) return false;
  if (r.right.sums.size || r.right.ints.size || symbolsOf(r.right.outer).has('n')) return false;
  return near(val(r.right.outer), st.answerValue);
}

/** une ligne déjà établie (le coup de pouce passe à la suivante) */
export function kriemEstablished(st: KRiemState, text: string): boolean {
  let A: string[];
  try {
    A = kriemLineAtoms(text);
  } catch {
    return false;
  }
  const t = kriemText(A);
  if (st.written.has(t)) return true;
  if (isIntegralLine(A)) return st.subs.some((s) => kintEstablished(s.ks, subText(s, text)));
  const r = readLine(A);
  if (r.k === 'eq') {
    const known = st.forms.get(subjectKey(r.members[0]!.outer));
    if (!known) return false;
    const keys = new Set(known.forms.map((f) => f.wkey));
    return r.members.slice(1).every((m) => keys.has(m.wkey));
  }
  if (r.k === 'lim') {
    const lim = st.lims.get(subjectKey(r.left.outer));
    if (!lim) return false;
    if (r.right.ints.size) {
      const I = [...r.right.ints.values()][0]!;
      return lim.ints.some((i) => st.subs[i.sub]?.text === I.text);
    }
    return lim.writings.some((w) => w.wkey === shapeOf(r.right.outer));
  }
  return false;
}

/** coup de pouce : la ligne du corrigé qui suit la dernière déjà écrite */
export function kriemNextMove(
  st: KRiemState,
): { line: Line; text: string; atoms: string[]; label: string } | null {
  if (st.concluded) return null;
  let from = 0;
  st.path.forEach((t, i) => {
    if (kriemEstablished(st, t)) from = i + 1;
  });
  for (let i = from; i < st.path.length; i++) {
    const t = st.path[i]!;
    if (kriemEstablished(st, t)) continue;
    const atoms = kriemLineAtoms(t);
    const v = kriemJudge(st, chipsOf(atoms), { verify: true });
    return { line: kriemLine(t), text: kriemText(atoms), atoms, label: v.rule?.label ?? '' };
  }
  return null;
}

export function kriemSolvePath(model: ScratchModel): {
  lines: { line: Line; text: string; atoms: string[]; v: KRiemVerdict }[];
  reached: boolean;
} {
  const st = createKRiemState(model);
  const lines: { line: Line; text: string; atoms: string[]; v: KRiemVerdict }[] = [];
  for (const t of st.path) {
    const atoms = kriemLineAtoms(t);
    const v = kriemJudge(st, chipsOf(atoms), { verify: true });
    if (v.kind !== 'step') break;
    kriemCommit(st, v);
    lines.push({ line: kriemLine(t), text: kriemText(atoms), atoms, v });
  }
  return { lines, reached: st.concluded };
}

/**
 * La limite de la suite par la somme de Riemann de l'énoncé (contrôle de la porte) ; null :
 * le départ n'est pas une somme de Riemann (un produit : on calcule pour de grands n).
 */
export function kriemCheckLimit(model: ScratchModel): {
  value: number;
  how: 'riemann' | 'numeric';
} {
  const st = createKRiemState(model);
  const r = semRiemOf(st.start);
  if (r) return { value: riemValue(r), how: 'riemann' };
  // un produit : la suite pour de grands n (convergence en 1 ÷ n, extrapolée)
  const a = defValue(st, 100);
  const b = defValue(st, 200);
  return { value: 2 * b - a, how: 'numeric' };
}

/* ------------------------------ palette ------------------------------ */

export const RIEMANN_OPS = [
  '+',
  '−',
  '×',
  '÷',
  '(',
  ')',
  '²',
  '^',
  '√',
  'exp',
  'ln',
  'cos',
  'sin',
  'π',
  '=',
  ARROW,
];

/**
 * Les bouts (D6 : l'énoncé et le corrigé, jamais les pièges ni les choix) : la suite, n,
 * l'indice, x, les bouts « Σ(k = 1 ; n) », « ∫(a ; b) », « dx », « [ », « ](a ; b) », les
 * nombres, les opérations utiles et « → ».
 */
export function kriemPaletteParts(model: ScratchModel): {
  names: string[];
  signs: string[];
  numbers: string[];
  ops: string[];
} {
  const spec = model.kernel!;
  const texts = [spec.start, ...(spec.path ?? [])];
  const atoms = texts.flatMap((t) => {
    try {
      return kriemLineAtoms(t);
    } catch {
      return [];
    }
  });
  const used = new Set(atoms);
  const X = spec.goal.target;
  const sums = [...new Set(atoms.filter(isSumAtom))];
  const indices = [...new Set(sums.map((s) => sumBoundsOf(s)!.index))];
  const ints = [...new Set(atoms.filter(isIntegralAtom))];
  const closes = [...new Set(atoms.filter(isBracketClose))];
  const diffs = [...new Set(atoms.filter(isDiffAtom))];
  const xs = diffs.map((d) => d.slice(1));
  const names = [
    X,
    'n',
    ...indices,
    ...xs,
    ...['u', 'u′', 'v′', 'v']
      .flatMap((h) => xs.map((x) => `${h}(${x})`))
      .filter((n) => used.has(n)),
    ...(used.has('e') ? ['e'] : []),
  ];
  const signs = [...sums, ...ints, ...diffs, ...(closes.length ? ['[', ...closes] : [])];
  const nums = [...new Set(atoms.filter((a) => /^\d+(,\d+)?$/.test(a)))];
  const value = (s: string) => Number(s.replace(',', '.'));
  nums.sort((a, b) => value(a) - value(b) || a.localeCompare(b));
  const always = new Set(['+', '−', '×', '÷', '(', ')', '=', ARROW]);
  const ops = RIEMANN_OPS.filter((o) => always.has(o) || used.has(o));
  const sups = [...new Set(atoms.filter((a) => /^[⁰¹³⁴⁵⁶⁷⁸⁹]+$/.test(a)))];
  if (sups.length)
    ops.splice(ops.indexOf('²') >= 0 ? ops.indexOf('²') + 1 : ops.indexOf('='), 0, ...sups);
  return { names: [...new Set(names)], signs, numbers: nums, ops };
}

/** les bouts d'une ligne de l'énoncé (affichage, recopie) */
export function kriemGivenAtoms(text: string): string[] {
  return kriemLineAtoms(text);
}
